import { useEffect, useRef, useState } from 'react'
import { makeCorePostRequest } from '../../../../../utils/api'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { CameraAlt as CameraIcon } from '@mui/icons-material'
import { Button, IconButton } from '@fullscreendigital/carrefour-dex-fe'
import { WebcamCapture } from 'components/core-ui/WebcamCapture'
import ImageIcon from '@mui/icons-material/Image'
import s from './ImageUpload.modules.scss'
import { resizeFileHigherQuality } from '../../../../../utils/helpers'
import { useOfflineTool } from 'components/core-ui/Offline'
import { AxiosError } from 'axios'
import { useNotification } from 'hooks'
import { videoConstraints } from './helpers'

const ImageUpload = ({
  uploadUrl,
  onSuccess,
  onError,
  onInternetConnectionError = () => {},
  isButton = false,
  label = 'Adauga imagine',
  btnStyle = {},
  setIsCameraOpen = null
}) => {
  const { showNotification } = useNotification()
  const offlineTool = useOfflineTool()
  const [showPhotoCapture, setShowPhotoCapture] = useState(false)
  const [file, setFile] = useState(null)
  const [open, setOpen] = useState(false)

  const inputFileRef = useRef<HTMLInputElement>(null)

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setShowPhotoCapture(false)
  }

  const saveFile = async (file: File): Promise<void> => {
    setShowPhotoCapture(false)

    try {
      const resizedFile = (await resizeFileHigherQuality({ file })) as Blob

      const formData = new FormData()
      formData.append('photoFile', resizedFile)
      console.log('originalImage', (file.size / (1024 * 1024)).toFixed(5), 'mb')
      console.log('resizedImage', (resizedFile.size / (1024 * 1024)).toFixed(5), 'mb')

      const response = await makeCorePostRequest(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      if (onSuccess) {
        setFile(file)
        onSuccess(response, file)
        handleClose()
      }
    } catch (err) {
      if (onError) {
        if (err?.code === AxiosError.ERR_NETWORK) {
          showNotification({
            message:
              'Conexiunea la internet a fost pierduta. Poza a fost salvata local si va fi incarcata cand veti avea conexiune la internet.',
            type: 'error'
          })

          // Store offline
          offlineTool.addOwnTask({
            type: `Adauga imagine`,
            data: err.config.data as FormData,
            url: uploadUrl,
            method: 'post'
          })
          setFile(file)
          handleClose()
          onInternetConnectionError && onInternetConnectionError()

          return
        }

        onError(err)
      }
    }
  }

  useEffect(() => {
    const handleOfflineTaskSuccess = event => {
      const { task } = event.detail
      if (task.url === uploadUrl) {
        const imageFile = new File([new Blob([JSON.stringify(task.data)])], 'image.JPEG', {
          type: 'image/png'
        })

        setFile(imageFile)
        if (onSuccess) {
          onSuccess(task, imageFile)
        }
      }
    }

    window.addEventListener('offlineTaskSuccess', handleOfflineTaskSuccess)

    return () => {
      window.removeEventListener('offlineTaskSuccess', handleOfflineTaskSuccess)
    }
  }, [uploadUrl, onSuccess])

  return process.env.NODE_ENV !== 'uat' && process.env.NODE_ENV !== 'development' ? (
    <div>
      {file ? (
        <Box>
          <IconButton
            style={{ ...btnStyle, color: 'green', '&:hover': { color: 'green !important' } }}
            onClick={() => {
              setFile(null)
              setShowPhotoCapture(true)
              setIsCameraOpen?.(true)
            }}
          >
            <CameraIcon />
          </IconButton>
        </Box>
      ) : (
        <>
          {showPhotoCapture ? (
            <Box>
              <WebcamCapture
                buttonStyle={{ width: '100%' }}
                onImageCapture={async imageSrc => {
                  fetch(imageSrc)
                    .then(res => res.blob())
                    .then(async blob => {
                      const file = new File([blob], 'image.JPEG', { type: 'image/png' })
                      await saveFile(file)
                      setIsCameraOpen && setIsCameraOpen(false)
                    })
                }}
                videoConstraints={videoConstraints}
                style={{ maxWidth: '100%' }}
              />
            </Box>
          ) : isButton ? (
            <Button
              type="button"
              variant="outlined"
              label={label}
              style={{ ...btnStyle, width: '100%' }}
              onClick={() => setShowPhotoCapture(true)}
            />
          ) : (
            <IconButton
              onClick={() => {
                setShowPhotoCapture(true)
                setIsCameraOpen && setIsCameraOpen(true)
              }}
              style={{ ...btnStyle }}
            >
              <CameraIcon />
            </IconButton>
          )}
        </>
      )}
    </div>
  ) : (
    <div>
      {file ? (
        <Box>
          <IconButton
            style={{ ...btnStyle, color: 'green', '&:hover': { color: 'green !important' } }}
            onClick={handleClickOpen}
          >
            <CameraIcon />
          </IconButton>
        </Box>
      ) : (
        <>
          {isButton ? (
            <Button
              type="button"
              variant="outlined"
              label={label}
              style={{ ...btnStyle, width: '100%' }}
              onClick={handleClickOpen}
            />
          ) : (
            <IconButton onClick={handleClickOpen} style={{ ...btnStyle }}>
              <CameraIcon />
            </IconButton>
          )}
        </>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth={true}
        style={{ textAlign: 'center' }}
      >
        <DialogTitle>Adauga imagine</DialogTitle>
        <DialogContent style={{ paddingBottom: '0', display: 'flex', flexDirection: 'column' }}>
          {showPhotoCapture ? (
            <Box className={s.webcamContainer}>
              <WebcamCapture
                buttonStyle={{ width: '100%' }}
                onImageCapture={async imageSrc => {
                  fetch(imageSrc)
                    .then(res => res.blob())
                    .then(async blob => {
                      const file = new File([blob], 'image.JPEG', { type: 'image/png' })
                      await saveFile(file)
                    })
                }}
                videoConstraints={videoConstraints}
                style={{ maxWidth: '100%' }}
                showTorchToggle
              />
            </Box>
          ) : (
            <>
              <IconButton
                variant="outlined"
                onClick={() => setShowPhotoCapture(true)}
                style={{ marginBottom: '16px' }}
                startIcon={<ImageIcon />}
              >
                Captura foto
              </IconButton>
              <IconButton
                variant="outlined"
                onClick={() => inputFileRef?.current?.click()}
                startIcon={<ImageIcon />}
                sx={{ marginBottom: '16px' }}
              >
                Incarca foto
              </IconButton>
              <input
                ref={inputFileRef}
                style={{ display: 'none' }}
                type="file"
                accept="image/*"
                onChange={e => {
                  if (e.target.files) {
                    saveFile(e.currentTarget.files[0])
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} label="">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ImageUpload
